import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { MagnifyingGlass, FileText, Users } from '@phosphor-icons/react';
import { FileSearch } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

const STATUS_COLORS = {
  'Draft': 'bg-[#F1F5F9] text-[#475569]',
  'Pending Accounting Manager Approval': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending General Manager Approval': 'bg-[#FEF9C3] text-[#854D0E]',
  'Approved': 'bg-[#DCFCE7] text-[#166534]',
  'Rejected': 'bg-[#FEE2E2] text-[#991B1B]',
  'Cancelled': 'bg-[#F1F5F9] text-[#475569]'
};

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localQuery, setLocalQuery] = useState(query);

  useEffect(() => {
    if (!query.trim()) return;
    setLoading(true);
    api.get('/search', { params: { q: query } })
      .then(r => setResults(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (localQuery.trim()) {
      setSearchParams({ q: localQuery.trim() });
    }
  };

  const totalResults = results
    ? (results.payments?.length || 0) + (results.vendors?.length || 0) + (results.contracts?.length || 0)
    : 0;

  return (
    <div className="space-y-6" data-testid="search-results-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Search Results</h1>
        <p className="text-sm text-[#475569] mt-1">
          {loading ? 'Searching...' : query ? `${totalResults} results for "${query}"` : 'Enter a search term'}
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-lg">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            placeholder="Search requests, vendors, contracts..."
            className="pl-9 rounded-sm h-10"
            data-testid="search-input"
          />
        </div>
        <button type="submit" className="px-4 h-10 bg-[#0055FF] text-white text-sm font-medium rounded-sm hover:bg-[#0043CC] transition-colors" data-testid="search-btn">
          Search
        </button>
      </form>

      {loading && (
        <div className="space-y-4 animate-pulse">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-sm" />)}
        </div>
      )}

      {results && !loading && (
        <div className="space-y-6">
          {/* Payment Requests */}
          {results.payments?.length > 0 && (
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FileText size={16} className="text-[#0055FF]" />
                  Payment Requests ({results.payments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F1F5F9]">
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Code</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Description</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold text-right">Amount</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.payments.map(p => (
                      <TableRow key={p.id} className="hover:bg-[#F8FAFC]">
                        <TableCell>
                          <Link to={`/payments/${p.id}`} className="text-[#0055FF] hover:underline font-medium text-sm" data-testid={`search-payment-${p.request_code}`}>
                            {p.request_code}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm text-[#475569] max-w-xs truncate">{p.description}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{fmt(p.amount)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.05em] font-semibold rounded-sm ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                            {p.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Vendors */}
          {results.vendors?.length > 0 && (
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Users size={16} className="text-[#0055FF]" />
                  Vendors ({results.vendors.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F1F5F9]">
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Code</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Name</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Category</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Contact</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.vendors.map(v => (
                      <TableRow key={v.id} className="hover:bg-[#F8FAFC]">
                        <TableCell className="text-sm font-mono">{v.vendor_code}</TableCell>
                        <TableCell>
                          <Link to={`/vendors/${v.id}`} className="text-[#0055FF] hover:underline font-medium text-sm" data-testid={`search-vendor-${v.vendor_code}`}>
                            {v.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{v.category}</TableCell>
                        <TableCell className="text-sm text-[#475569]">{v.contact_person}</TableCell>
                        <TableCell>
                          <span className={`text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm ${v.status === 'active' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                            {v.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Contracts */}
          {results.contracts?.length > 0 && (
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FileSearch size={16} className="text-[#0055FF]" />
                  Contracts ({results.contracts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F1F5F9]">
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Code</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Title</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold text-right">Amount</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">End Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.contracts.map(c => (
                      <TableRow key={c.id} className="hover:bg-[#F8FAFC]">
                        <TableCell className="text-sm font-mono">{c.contract_code}</TableCell>
                        <TableCell>
                          <Link to={`/contracts/${c.id}`} className="text-[#0055FF] hover:underline font-medium text-sm" data-testid={`search-contract-${c.contract_code}`}>
                            {c.title}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">{fmt(c.contract_amount)}</TableCell>
                        <TableCell className="text-sm">{c.end_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* No results */}
          {totalResults === 0 && (
            <div className="text-center py-12">
              <MagnifyingGlass size={48} className="mx-auto text-[#94A3B8] mb-3" />
              <p className="text-[#475569] text-sm">No results found for "{query}"</p>
              <p className="text-[#94A3B8] text-xs mt-1">Try a different search term or check the spelling</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
